import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    console.log(dialog.querySelector('video'));

    document.body.style.width = '100%';
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;

    dialog.showModal();
    this.dispatchEvent(new DialogOpenEvent());

    // Wait until the next tick to add the event listeners to avoid race condition
    // when `showDialog` is called within a click event listener.
    setTimeout(() => {
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    document.body.style.width = '';
    const scrollY = document.body.style.top;
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: parseInt(scrollY) * -1, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDialogElement || event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);


export class VideoDialogComponent extends DialogComponent {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    this.#onOpen;
    this.#onClose;
    this.openButtons = this.querySelectorAll('.video-button-play');
    this.addEventListener(DialogOpenEvent.eventName, this.#onOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#onClose);

    if(this.openButtons){
      this.openButtons.forEach(button => {
        button.addEventListener('click', this.showDialog.bind(this));
      });
    }
  }

  disconnectedCallback() {
    this.removeEventListener(DialogOpenEvent.eventName, this.#onOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#onClose);
    super.disconnectedCallback();
  }

  #onOpen = () => {
    const video = this.refs.dialog?.querySelector('video');
    if (video && typeof video.play === 'function') {
      video.play();
    }
  };

  #onClose = () => {
    const video = this.refs.dialog?.querySelector('video');
      if (video && typeof video.pause === 'function') {
        video.pause();
        // Reset to start on close     
        video.currentTime = 0;
      }
  };
}

if (!customElements.get('video-dialog-component')) {
  customElements.define('video-dialog-component', VideoDialogComponent);
}


export class InstructionsDialogComponent extends DialogComponent {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();
    this.initialHtml = this.querySelector('.vp-uploads-card__link')?.innerHTML;
    this.querySelector('.instructions-modal__footer-submit')?.addEventListener('click', this.#onSubmit.bind(this));
    this.querySelector('.instructions-modal__footer-clear')?.addEventListener('click', this.#onClear.bind(this));
    this.querySelector('.instructions-modal__close')?.addEventListener('click', this.#onClose.bind(this));
    this.addEventListener(DialogOpenEvent.eventName, this.#onOpen);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.querySelector('.instructions-modal__footer-submit')?.removeEventListener('click', this.#onSubmit.bind(this));
    this.querySelector('.instructions-modal__footer-clear')?.removeEventListener('click', this.#onClear.bind(this));
    this.querySelector('.instructions-modal__close')?.removeEventListener('click', this.#onClose.bind(this));
    this.removeEventListener(DialogOpenEvent.eventName, this.#onOpen);
  }

  #onOpen = () => {
    const textarea = this.refs.dialog?.querySelector('textarea[data-upload-instructions]');
    if(!textarea) return;

    textarea.focus();
  };
  
  #onClose = () => {
    const textarea = this.refs.dialog?.querySelector('textarea[data-upload-instructions]');
    if(!textarea) return;

    const textareaInput = this.parentElement?.querySelector(`input[id="${textarea.getAttribute('data-instructions-id')}"]`);
    
    if(!textareaInput || !(textareaInput instanceof HTMLInputElement)) return;
    if(textareaInput.value === textarea.value){
      this.closeDialog();
      return;
    };

    textarea.value = textareaInput.value;

    const btn = this.querySelector('[data-add-instructions]');

    if (btn instanceof HTMLButtonElement && (textareaInput.value == '' || textareaInput.value == null || textareaInput.value == undefined)) {
      btn.innerHTML = this.initialHtml || 'Add Instructions';
    }

    this.closeDialog();
  };

  #onSubmit = () => {
    const textarea = this.refs.dialog?.querySelector('textarea[data-upload-instructions]');

    if(!textarea) return;

    const textareaInput = this.parentElement?.querySelector(`input[id="${textarea.getAttribute('data-instructions-id')}"]`);

    console.log(textareaInput);

    if(!textareaInput || !(textareaInput instanceof HTMLInputElement)) return;

    textareaInput.value = textarea.value;

    if(textareaInput.value == '' || textareaInput.value == null || textareaInput.value == undefined){
      const btn = this.querySelector('[data-add-instructions]');
      if (btn instanceof HTMLButtonElement) {
        btn.innerHTML = this.initialHtml || 'Add Instructions';
      }
    } else {
      const btn = this.querySelector('[data-add-instructions]');
      if (btn instanceof HTMLButtonElement) {
        btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-pencil-icon lucide-pencil"><path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"/><path d="m15 5 4 4"/></svg> Edit Details';
      }
    }

    this.closeDialog();
  };

  #onClear = () => {
    const textarea = this.refs.dialog?.querySelector('textarea[data-upload-instructions]');
    if(!textarea) return;
    
    const textareaInput = this.parentElement?.querySelector(`input[id="${textarea.getAttribute('data-instructions-id')}"]`);

    if(!textareaInput || !(textareaInput instanceof HTMLInputElement)) return;

    textarea.value = '';
    textareaInput.value = textarea.value;

    const btn = this.querySelector('[data-add-instructions]');
    if (btn instanceof HTMLButtonElement) {
      btn.innerHTML = this.initialHtml || 'Add Instructions';
    }
  };
}

if (!customElements.get('instructions-dialog-component')) {
  customElements.define('instructions-dialog-component', InstructionsDialogComponent);
}
